import React, { useState } from 'react';
import { apiFetch } from './utils/api';

function ReceiveGoodsModal({ po, onClose, onSuccess }) {
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split('T')[0]);
  const [warehouseId, setWarehouseId] = useState(po.warehouse_id || 1);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState(
    po.items?.map(item => ({
      ...item,
      received_quantity: parseFloat(item.quantity)
    })) || []
  );
  const [submitting, setSubmitting] = useState(false);
  
  const handleQuantityChange = (itemId, newQty) => {
    setItems(items.map(item => 
      item.id === itemId 
        ? { ...item, received_quantity: parseFloat(newQty) || 0 }
        : item
    ));
  };
  
  const handleReceive = async () => {
    setSubmitting(true);
    
    try {
      const response = await apiFetch(
        `/api/purchase-orders/${po.id}/receive`,
        {
          method: 'POST',
          body: JSON.stringify({
            received_date: receiptDate,
            warehouse_id: warehouseId,
            notes,
            items: items.map(item => ({
              product_id: item.product_id,
              received_quantity: item.received_quantity
            }))
          })
        }
      );
      
      const result = await response.json();
      
      if (response.ok) {
        onSuccess();
      } else {
        alert(result.error || 'Failed to receive goods');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error receiving goods:', error);
      alert('Failed to receive goods');
      setSubmitting(false);
    }
  };
  
  const totalOrdered = items.reduce((sum, item) => sum + parseFloat(item.quantity), 0);
  const totalReceiving = items.reduce((sum, item) => sum + item.received_quantity, 0);
  const isPartialReceipt = totalReceiving < totalOrdered;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Receive Goods</h2>
            <p className="text-green-100 text-sm">{po.po_number} - {po.supplier_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-green-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Receipt Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Receipt Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt Date
                </label>
                <input
                  type="date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Warehouse
                </label>
                <select
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value={1}>Main Warehouse</option>
                  <option value={2}>Workshop</option>
                  <option value={3}>Van Stock</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Receipt notes, delivery reference, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Allocation Info */}
          {(po.job_number || po.sales_order_number) && (
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>Auto-Allocation:</strong> Stock will be automatically allocated to{' '}
                    <strong>
                      {po.job_number ? `Job #${po.job_number}` : `Sales Order #${po.sales_order_number}`}
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Items Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Items to Receive</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordered
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receiving
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {item.product_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {item.product_code}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {parseFloat(item.quantity).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          max={item.quantity}
                          step="0.01"
                          value={item.received_quantity}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent text-right"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-gray-900">
                      Total:
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                      {totalOrdered.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-green-700 text-right">
                      {totalReceiving.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            {isPartialReceipt && (
              <div className="mt-3 bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-800">
                      <strong>Partial Receipt:</strong> Receiving {totalReceiving.toFixed(2)} of {totalOrdered.toFixed(2)} items. 
                      PO will remain open for remaining items.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          
          <button
            onClick={handleReceive}
            disabled={submitting || totalReceiving === 0}
            className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Receive {totalReceiving.toFixed(2)} Items</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReceiveGoodsModal;
